import { Platform } from "react-native";
import { autoMetaDescriptionAtom, pageTitleAtom } from "../atoms";
import { useAtom, useSetAtom } from "jotai";
import { useFocusEffect } from "@react-navigation/native";
import { isFriendlyDate, isYYYYMMDD } from "../components/Tabs/helpers";
import { seoTitleAndDescriptions } from "../utils/SeoTitlesAndDescriptions";
import { useCallback } from "react";
import { Tab } from "../components/Tabs";
import { fullCalendarDate } from "../utils/TimeCalculator";
import { removeCountry } from "../utils/removeCountry";

// This hook automates the process of setting the document title and meta description
// of a web page based on the current page, tab, and other contextual information.
// It takes into account whether a "hard" title and description are provided or if
// it should generate them dynamically based on the page's content and the selected tab.

// Example usage with tabs
//   UseAutoMetaTags({page: 'my-page', tabs: tabs, activeTab: 1});

// Example usage with hard title and description
//   UseAutoMetaTags({page: 'my-page', hardTitle: 'My Hard Title', hardDescription: 'My Hard Description'});

// weekday friends mapping

interface PageTags {
  title: (val: string, additionalData?: { [key: string]: any }) => string;
  description: (val: string, additionalData?: { [key: string]: any }) => string;
}

interface PageTagMap {
  [key: string]: PageTags;
}

const pageTagMap: PageTagMap = {
  Home: {
    //title: Today's Horse Racing News & Results | Racing TV
    //description: Racing TV offers unrivalled access to live horse racing, news & results across the UK, Ireland and beyond. Get the latest betting odds, tips & expert analysis.
    title: (val) => {
      return `Today's Horse Racing News & Results | Racing TV`;
    },
    description: (val) => {
      return `Racing TV offers unrivalled access to live horse racing, news & results across the UK, Ireland and beyond. Get the latest betting odds, tips & expert analysis.`;
    },
  },
  Watch: {
    // title: Watch Today's Live Horse Racing Streaming | Racing TV
    // description: Watch today's live horse racing in stunning HD from Racing TV - the home of British and Irish racing.
    title: (val) => {
      return `Watch Today's Live Horse Racing Streaming | Racing TV`;
    },
    description: (val) => {
      return `Watch today's live horse racing in stunning HD from Racing TV - the home of British and Irish racing.`;
    },
  },
  Replays: {
    title: (val) => {
      // val can be today, tomorrow, mon-sun, yyyy-mm-dd and also big-races, most-watched
      if (isFriendlyDate(val)) {
        return `Watch ${val?.capitalize()}'s Horse Racing Replays | Racing TV`;
      } else if (isYYYYMMDD(val)) {
        return `Watch Horse Racing Replays from ${fullCalendarDate(
          val
        )} | Racing TV`;
      } else {
        // assumes its now big-races or most watched
        console.log("val", val);
        try {
          return seoTitleAndDescriptions.replays.titles[val]();
        } catch {
          // debugger;
        }
      }
    },
    description: (val) => {
      // val can be today, tomorrow, mon-sun, yyyy-mm-dd and also big-races, most-watched
      if (isFriendlyDate(val)) {
        return `Watch ${val}'s horse racing replays in stunning HD with Racing TV. Catch up on all the racing action across the UK and Ireland.`;
      } else if (isYYYYMMDD(val)) {
        return `Watch horse racing replays from ${fullCalendarDate(
          val
        )} in stunning HD with Racing TV. Catch up on all the racing action across the UK and Ireland.`;
      } else {
        // assumes its now big-races or most watched
        console.log("val", val);
        try {
          return seoTitleAndDescriptions.replays.descriptions[val]();
        } catch {}
      }
    },
  },
  OnDemand: {
    // title: On Demand | Racing TV
    // description: Watch all the latest horse racing features, interviews and episodes from Racing TV's On Demand service - the home of British and Irish racing.
    title: (val) => {
      return `On Demand | Racing TV`;
    },
    description: (val) => {
      return `Watch all the latest horse racing features, interviews and episodes from Racing TV's On Demand service - the home of British and Irish racing.`;
    },
  },
  IndividualOnDemand: {
    title: (val, additionalData) =>
      //On Demand: Video Title
      `On Demand: ${additionalData?.title}`,
    description: (val, additionalData) =>
      //On Demand horse racing from Racing TV: Video Title
      `On Demand horse racing from Racing TV: ${additionalData?.title}`,
  },
  CatchUp: {
    // title: Catch Up | Yesterday | Racing TV
    // description: Watch yesterday's horse racing Catch Up videos from Racing TV - the home of British and Irish racing.
    title: (val) => {
      // omit today if present
      return `Catch Up | ${
        val && val != "today" ? `${val?.capitalize()} |` : ""
      } Racing TV`;
    },
    description: (val) => {
      return `Watch ${
        val && val != "today" ? `${val?.capitalize()}'s` : "the latest"
      } horse racing Catch Up videos from Racing TV - the home of British and Irish racing.`;
    },
  },
  CombinedOnDemand: {
    title: (val, additionalData) => {
      if (additionalData.title) {
        return `Catch Up: ${additionalData?.title}`;
      } else {
        return `On Demand | ${additionalData?.category?.capitalize()} | Racing TV`;
      }
    },
    description: (val, additionalData) => {
      if (additionalData.title) {
        return `Catch Up horse racing from Racing TV: ${additionalData?.title}`;
      } else {
        return `Watch ${additionalData?.title?.capitalize()} from Racing TV's On Demand service - the home of British and Irish racing.`;
      }
    },
  },

  Racecards: {
    title: (val) => {
      if (isYYYYMMDD(val)) {
        return `Racecards | ${fullCalendarDate(val)}`;
      }

      return `${val?.capitalize()}'s Horse Racing Racecards | Racing TV`;
    },
    description: (val) => {
      if (isYYYYMMDD(val)) {
        return `View horse racing racecards on ${fullCalendarDate(
          val
        )} for every racecourse in the UK & Ireland, and for the biggest racing fixtures in the international calendar.`;
      }
      return `View ${val}'s horse racing racecards for every racecourse in the UK & Ireland, and for the biggest racing fixtures in the international calendar.`;
    },
  },
  JockeyTrainerHorse: {
    title: (val, additionalData) =>
      // Daniel Tudhop | Race Record, Statistics & Wins | Racing TV
      `${additionalData?.name} | Race Record, Statistics & Wins | Racing TV`,
    description: (val, additionalData) =>
      // Daniel Tudhope profile, complete with race record, results, future entries, wins, statistics and more.
      `${additionalData?.name} profile, complete with race record, results, future entries, wins, statistics and more.`,
  },

  "tv-schedule": {
    title: (val) =>
      `${val?.capitalize()}'s Horse Racing TV Schedule | Racing TV`,
    description: (val) =>
      `View ${val}'s Racing TV schedule. Set alerts and stay up to date with all upcoming races throughout the UK and Ireland.`,
  },

  "rtv-play-schedule": {
    title: (val) =>
      `${val?.capitalize()}'s Horse Racing TV Play Schedule | Racing TV`,
    description: (val) =>
      `View ${val}'s Racing TV Play schedule. Set alerts and stay up to date with all upcoming races throughout the UK and Ireland.`,
  },
  // Antepost: {
  // title: Antepost Betting & Odds | Jan 2023 | Racing TV
  // description: Antepost betting & odds for all January races throughout the UK & Ireland. Place your bets, access the latest racecards, odds, results & more.
  //   title: (val, additionalData) => {
  //     if (additionalData.month && additionalData.year) {
  //       return `Antepost Betting & Odds | ${additionalData.month.capitalize()} ${
  //         additionalData.year
  //       } | Racing TV`;
  //     }
  //     return `Antepost Betting & Odds | Racing TV`;
  //   },
  //   description: (val, additionalData) => {
  //     if (additionalData.month && additionalData.year) {
  //       const allMonths = [
  //         "january",
  //         "february",
  //         "march",
  //         "april",
  //         "may",
  //         "june",
  //         "july",
  //         "august",
  //         "september",
  //         "october",
  //         "november",
  //         "december",
  //       ];
  //       const month = allMonths.find((m) => m.includes(additionalData.month));
  //       return `Antepost betting & odds for all ${month.capitalize()} races throughout the UK & Ireland. Place your bets, access the latest racecards, odds, results & more.`;
  //     }
  //     return `Antepost betting & odds for all races throughout the UK & Ireland. Place your bets, access the latest racecards, odds, results & more.`;
  //   },
  // },
  // IndividualAntepostRacecard: {
  //   title: (val, additionalData) => {
  //     // Dewhurst Stakes Antepost Racecard | 18 January 2023 13:15
  //     return `${additionalData.track} Antepost Racecard | ${additionalData.date} ${additionalData.hhmm}`;
  //   },
  //   description: (val, additionalData) => {
  //     // Get the latest antepost racecard from the 13:15 at Dewhurst Stakes on the 18th January 2023.
  //     return `Get the latest antepost racecard from the ${additionalData.hhmm} at ${additionalData.track} on the ${additionalData.date}`;
  //   },
  // },
  TodaysRunners: {
    // title: Today's Runners and Riders | Racing TV
    // description: View today's declared runners and riders for every racecourse across the UK & Ireland. Access the latest racecards, odds, results & more.
    title: (val) => {
      return `Today's Runners and Riders | Racing TV`;
    },
    description: (val) => {
      return `View today's declared runners and riders for every racecourse across the UK & Ireland. Access the latest racecards, odds, results & more.`;
    },
  },
  TomorrowsRunners: {
    // title: Tomorrow's Runners and Riders | Racing TV
    // description: View tomorrow's declared runners and riders for every racecourse across the UK & Ireland. Access the latest racecards, odds, results & more.
    title: (val) => {
      return `Tomorrow's Runners and Riders | Racing TV`;
    },
    description: (val) => {
      return `View tomorrow's declared runners and riders for every racecourse across the UK & Ireland. Access the latest racecards, odds, results & more.`;
    },
  },
  NonRunners: {
    // title: Today's Horse Racing Non Runners | Racing TV
    // description: View today's non runners for every racecourse across the UK & Ireland. Access the latest racecards, odds, results & more.
    title: (val) => {
      return `Today's Horse Racing Non Runners | Racing TV`;
    },
    description: (val) => {
      return `View today's non runners for every racecourse across the UK & Ireland. Access the latest racecards, odds, results & more.`;
    },
  },
  HorseRacingCalendar: {
    // title: Horse Racing Fixtures, Calendar & Meetings | Racing TV
    // description: View all the upcoming horse racing fixtures across the UK & Ireland in the Racing TV calendar.
    title: (val) => {
      return `Horse Racing Fixtures, Calendar & Meetings | Racing TV`;
    },
    description: (val) => {
      return `View all the upcoming horse racing fixtures across the UK & Ireland in the Racing TV calendar.`;
    },
  },
  IndividualRacecard: {
    title: (val, additionalData) => {
      // Newbury Racecard | 18 January 2023 13:15
      return `${removeCountry(additionalData.track)} Racecard | ${
        additionalData.date
      } ${additionalData.hhmm}`;
    },
    description: (val, additionalData) => {
      // View the <race name> racecard, <race time> at <racecourse> <date>.
      return `View the ${additionalData.raceName} racecard, ${
        additionalData.hhmm
      } at ${removeCountry(additionalData.track)} ${additionalData.date}`;
    },
  },

  Results: {
    title: (val) => {
      // val can be today, tomorrow, mon-sun, yyyy-mm-dd
      if (isFriendlyDate(val)) {
        if (val === "today") {
          return `Horse Racing Results Today | Today's Racing Results | Racing TV`;
        } else if (val === "yesterday") {
          return `Horse Racing Results Yesterday | Racing Results | Racing TV`;
        } else {
          return `${val?.capitalize()}'s Horse Racing Results | Racing Results ${val?.capitalize()} | Racing TV`;
        }
      } else if (isYYYYMMDD(val)) {
        return `Horse Racing Results from ${fullCalendarDate(val)} | Racing TV`;
      }
    },
    description: (val) => {
      // Get all the latest horse racing results and video replays from today's racing across the UK, Ireland and beyond.
      if (isFriendlyDate(val)) {
        return `Check out ${
          val === "today" ? "the latest" : `${val.capitalize()}s`
        } Horse Racing Results! Watch the all racing action & keep up to date with results from across the UK, Ireland and more.`;
      } else if (isYYYYMMDD(val)) {
        return `Get all the latest horse racing results and video replays from ${fullCalendarDate(
          val
        )} across the UK and Ireland in stunning HD with Racing TV.`;
      }
    },
  },
  FastResults: {
    title: (val) => {
      return "Fast Results | Fast Horse Racing Results | Racing TV";
    },
    description: (val) => {
      return "Check out our Fast Horse Racing Results! Watch the all racing action & keep up to date with results from across the UK, Ireland and more.";
    },
  },
  IndividualResult: {
    title: (val, additionalData) => {
      // Newbury Racing Results | 18 January 2023 13:15
      return `${removeCountry(additionalData.track)} Racing Results | ${
        additionalData.date
      } ${additionalData.hhmm}`;
    },
    description: (val, additionalData) => {
      // View the <race name> racing result, <race time> at <racecourse> <date>.
      return `View the ${additionalData.raceName} racing result, ${
        additionalData.hhmm
      } at ${removeCountry(additionalData.track)} ${additionalData.date}`;
    },
  },

  IndividualReplay: {
    title: (val, additionalData) => {
      // Kempton Park Replay | 24 April 2023 16:50
      return `${removeCountry(additionalData.track)} Replay | ${
        additionalData.date
      } ${additionalData.hhmm}`;
    },
    description: (val, additionalData) => {
      // Watch the <race name> racing replay, <race time> at <racecourse> <date>.
      return `Watch the ${additionalData.raceName} racing replay, ${
        additionalData.hhmm
      } at ${removeCountry(additionalData.track)} ${additionalData.date}`;
    },
  },

  RTVTipsters: {
    // title: Today's Horse Racing Tips | Racing TV
    title: (val) => {
      return `${val?.capitalize()}'s Horse Racing Tips | Racing TV`;
    },
    // description: Get our expert analysis and free horse racing tips for today's races throughout the UK and Ireland.
    description: (val) => {
      return `Get our expert analysis and free horse racing tips for ${val?.capitalize()}'s races throughout the UK and Ireland.`;
    },
  },
  // AllTips: {
  // if val is today, return `All Horse Racing Tips For Today | Racing TV`
  // if val is tomorrow, return `Horse Racing Tips For Tomorrow | Racing TV Tipsters`
  // if val is key, return Key To Racing Icons | Racing TV
  // title: (val) => {
  // if (val == "today") {
  // return `All Horse Racing Tips For Today | Racing TV`;
  // } else if (val == "tomorrow") {
  // return `Horse Racing Tips For Tomorrow | Racing TV Tipsters`;
  // } else {
  // return `Key To Racing Icons | Racing TV`;
  // }
  // },
  // description: (val) => {
  // if val is today, return Get our expert analysis and free horse racing tips for all of tomorrow's races throughout the UK and Ireland.
  // if val is tomorrow, return Get our expert analysis and free horse racing tips for all of tomorrow's races throughout the UK and Ireland.
  // if val is key, return View our terminology for the varoous racing icons used throughout our tips and expert analysis.
  // if (val == "today") {
  // return `Get our expert analysis and free horse racing tips for all of today's races throughout the UK and Ireland.`;
  // } else if (val == "tomorrow") {
  // return `Get our expert analysis and free horse racing tips for all of tomorrow's races throughout the UK and Ireland.`;
  // } else {
  // return `View our terminology for the various racing icons used throughout our tips and expert analysis.`;
  // }
  // },
  // },
  NapOfTheDay: {
    // title: Nap Of The Day | Racing TV
    // description: Get our expert analysis and NAP Of The Day for all the upcoming races throughout the UK and Ireland.
    title: (val) => {
      return `Nap Of The Day | Racing TV`;
    },
    description: (val) => {
      return `Get our expert analysis and NAP Of The Day for all the upcoming races throughout the UK and Ireland.`;
    },
  },
  IndividualTipster: {
    title: (val, additionalData) =>
      `${additionalData.name.capitalize()}'s Tips For ${val?.capitalize()} | Racing TV`,
    description: (val, additionalData) =>
      `${additionalData.name.capitalize()}'s horse racing tips across the UK and Ireland from Racing TV columnist ${additionalData?.capitalize()}.`,
  },

  AllNews: {
    title: (val, additionalData) => {
      // debugger;
      if (!additionalData?.date) return `Today's Horse Racing News | Racing TV`;

      if (isYYYYMMDD(additionalData?.date)) {
        return `Horse Racing News From ${fullCalendarDate(
          additionalData?.date
        )} | Racing TV`;
      }
    },
    description: (val, additionalData) => {
      if (!additionalData?.date)
        return `Racing TV brings you all the latest horse racing news from the UK, Ireland & beyond. Watch or re-live every moment in stunning HD.`;

      if (isYYYYMMDD(additionalData?.date)) {
        return `Racing TV brings you all the news on ${fullCalendarDate(
          additionalData?.date
        )} horse racing news from the UK, Ireland & beyond. Watch or re-live every moment in stunning HD.`;
      }
    },
  },

  NewsCategory: {
    // title: Racing TV News | ${additionalData.category.capitalize()}
    // description: ${additionalData.category.capitalize()} horse racing news from Racing TV. Watch or re-live every moment in stunning HD.
    title: (val, additionalData) => {
      if (additionalData?.category == "Latest") {
        return `The Latest & Breaking Horse Racing News | Racing TV`;
      }
      return `Racing TV News | ${additionalData?.category?.capitalize()}`;
    },
    description: (val, additionalData) => {
      if (additionalData?.category == "Highlights") {
        return `Racing TV brings you all the latest horse racing news and highlights from the UK, Ireland & beyond. Watch or re-live every moment in stunning HD.`;
      } else if (additionalData?.category == "Most Viewed") {
        return `Read our Most Viewed articles from the world of horse Racing with Racing TV - bringing you all the latest racing news from the UK, Ireland & beyond.`;
      } else if (additionalData?.category == "Latest") {
        return `Racing TV brings you all the latest and breaking horse racing news from the UK, Ireland & beyond. Watch or re-live every moment in stunning HD.`;
      }

      // return `${additionalData.category.capitalize()} horse racing news from Racing TV. Watch or re-live every moment in stunning HD.`;
    },
  },

  Columnists: {
    // title: Racing TV Columnists | Articles, Tips & Biographies
    title: (val) => {
      return `Racing TV Columnists | Articles, Tips & Biographies`;
    },

    // description: Read the latest articles and biographies from the regular columnists at Racing TV.
    description: (val) => {
      return `Read the latest articles and biographies from the regular columnists at Racing TV.`;
    },
  },
  IndividualColumnist: {
    // title: Dave Devison | Racing TV
    title: (val, additionalData) => {
      return `${additionalData?.name} | Racing TV`;
    },

    // description: Today's horse racing news across the UK and Ireland from Racing TV columnist Dave Nevison.
    description: (val, additionalData) => {
      return `Read all of ${additionalData?.name}'s latest Racing TV columns. Featuring key insight, tips, and horses to follow.`;
    },
  },

  // StableTours: {
  // title: Horse Racing Stable Tours | Racing TV
  // title: (val) => {
  //   return `Horse Racing Stable Tours | Racing TV`;
  // },
  // description: We've been busy visiting yards and tapping into the minds of our experts. Get a head start for the weeks and months ahead with our stable tours.
  //   description: (val) => {
  //     return `We've been busy visiting yards and tapping into the minds of our experts. Get a head start for the weeks and months ahead with our stable tours.`;
  //   },
  // },

  FreeBets: {
    // title: Horse Racing Free Bets & Bookmaker Offers | Racing TV
    title: (val) => {
      return `Horse Racing Free Bets & Bookmaker Offers | Racing TV`;
    },
    // description: Racing TV provides the best horse racing free bets and bookmaker sign up offers available – all from our trusted bookmaker partners.
    description: (val) => {
      return `Racing TV provides the best horse racing free bets and bookmaker sign up offers available – all from our trusted bookmaker partners.`;
    },
  },

  Benefits: {
    // title: Racing TV Membership Benefits
    title: (val) => {
      return `Racing TV Membership Benefits`;
    },
    // description: Make the most of your Racing TV membership with Racing TV Club days, exclusive racecourse offers, Winter Season Tickets, Racing Replays & Club Magazine.
    description: (val) => {
      return `Make the most of your Racing TV membership with Racing TV Club days, exclusive racecourse offers, Winter Season Tickets, Racing Replays & Club Magazine.`;
    },
  },
  RtvExtra: {
    // title: Racing TV Extra
    title: (val) => {
      return `Racing TV Extra`;
    },

    // description: Racing TV Extra brings uninterrupted coverage via live dedicated racecourse streams from all 61 of our British and Irish racecourses.
    description: (val) => {
      return `Racing TV Extra brings uninterrupted coverage via live dedicated racecourse streams from all 61 of our British and Irish racecourses.`;
    },
  },
  ClubDays: {
    // title: Racing TV Club Days
    title: (val) => {
      return `Racing TV Club Days`;
    },
    // description: Our Racing TV Club Days gives you access to over 100 fixtures in a combination of both weekdays and over 45 weekends, both in the UK and Ireland.
    description: (val) => {
      return `Our Racing TV Club Days gives you access to over 100 fixtures in a combination of both weekdays and over 45 weekends, both in the UK and Ireland.`;
    },
  },
  Magazine: {
    // title: Racing TV Club Magazine
    title: (val) => {
      return `Racing TV Club Magazine`;
    },
    // description: Read the Racing TV magazine, with access to the latest and past editions of our quartlery roundup, containgin all the latest news and expert analysis.
    description: (val) => {
      return `Read the Racing TV magazine, with access to the latest and past editions of our quartlery roundup, containgin all the latest news and expert analysis.`;
    },
  },
  Rewards4Racing: {
    // title: Rewards4Racing | Racing TV
    title: (val) => {
      return `Rewards4Racing | Racing TV`;
    },

    // description: Rewards4Racing members can collect and spend Rewards4Racing points on Racing TV’s products and services, including Day Pass.
    description: (val) => {
      return `Rewards4Racing members can collect and spend Rewards4Racing points on Racing TV’s products and services, including Day Pass.`;
    },
  },
  Podcasts: {
    // title: Racing TV Podcasts
    // description: Listen to the latest Racing TV podcasts. Also available via Apple Podcasts, Spotify or Audiboom.
    title: (val) => {
      return `Racing TV Podcasts`;
    },
    description: (val) => {
      return `Listen to the latest Racing TV podcasts. Also available via Apple Podcasts, Spotify or Audiboom.`;
    },
  },
  FestivalHub: {
    title: (val, data) => {
      if (val == "overview") {
        return `${data?.title}: The Latest News, Tips & Results.`;
      } else if (val == "news") {
        return `The Latest ${data?.title} News | Racing TV`;
      } else if (val == "free-bets") {
        return `${data?.title} Free Bets & Bookmaker Offers | Racing TV`;
      } else if (val == "racecards") {
        return `${data?.title} Racecards | Racing TV`;
      } else if (val == "results" || data.section == "results") {
        return `${data?.title} Results | Racing TV`;
      } else if (val == "tips" || data.section == "tips") {
        return `${data?.title} Tips | Racing TV`;
      } else if (val == "replays" || data.section == "replays") {
        return `${data?.title} Replays | Racing TV`;
      } else if (val.startsWith("day") && !!data?.section) {
        return `${data?.title} ${data?.section?.capitalize()} | Racing TV`;
      } else {
        return `${data?.title} Festival ${val?.capitalize()}`;
      }
    },

    description: (val, data) => {
      const year = data?.festival?.dates
        ? data?.festival?.dates.start?.split("-")[0]
        : "";
      const day = data.day ? data.day.split("-").join(" ").capitalize() : "";

      if (val == "overview") {
        return `Racing TV brings you all the latest news from the ${data?.title} Festival - plus the latest results, tips, free bets and odds.`;
      } else if (val == "news") {
        return `Racing TV brings you all the latest horse racing news for the ${data?.title} - plus the latest odds, tips, free bets and results.`;
      } else if (val == "free-bets") {
        return `Racing TV provides the best horse racing free bets and bookmaker offers for the ${data?.title} - all from our trusted bookmaker partners.`;
      } else if (val == "racecards") {
        return `View all the racecards for ${
          day ? `${day} of` : ""
        } the ${year} ${
          data?.title
        }  - plus the latest tips, odds, free bets, news and results.`;
      } else if (val == "results") {
        return `Racing TV brings you all the latest results for ${
          day ? `${day} of` : ""
        } the ${year} ${
          data?.title
        } - plus the latest news, tips, free bets and odds.`;
      } else if (val == "tips" || data.section == "tips") {
        return `Racing TV brings you all the latest tips for ${
          day ? `${day} of` : ""
        } the ${year} ${
          data?.title
        } - plus the latest odds, free bets, news and results.`;
      } else if (val == "replays" || data.section == "replays") {
        return `Racing TV brings you all the latest replays for ${
          day ? `${day} of` : ""
        } the ${year} ${
          data?.title
        } - plus the latest odds, free bets, news and results.`;
      }
    },
  },
  Apps: {
    // title: Live Horse Racing, Results & Betting App: Racing TV
    title: (val) => {
      return `Live Horse Racing, Results & Betting App: Racing TV`;
    },
    // description: Take Racing TV with you via our range of bespoke apps - bringing you all the latest horse racing news, results, live streaming and betting features.
    description: (val) => {
      return `Take Racing TV with you via our range of bespoke apps - bringing you all the latest horse racing news, results, live streaming and betting features.`;
    },
  },
  ContactUs: {
    // title: Contact Us: Racing TV
    title: (val) => {
      return `Contact Us: Racing TV`;
    },
    // description: Please visit our website to find out how to get in touch with Racing TV Customer Services. Email or call us directly.
    description: (val) => {
      return `Please visit our website to find out how to get in touch with Racing TV Customer Services. Email or call us directly.`;
    },
  },
  HelpCenter: {
    // title: Welcome to the Racing TV Help Centre
    title: (val) => {
      return `Welcome to the Racing TV Help Centre`;
    },
    // description: Our friendly customer service team are on hand to assist with any of your queries. You can reach our team via email or the telephone numbers provided.
    description: (val) => {
      return `Our friendly customer service team are on hand to assist with any of your queries. You can reach our team via email or the telephone numbers provided.`;
    },
  },
  TermsAndConditions: {
    // title: Racing TV Terms and Conditions
    title: (val) => {
      return `Racing TV Terms and Conditions`;
    },
    // description: Please read our terms and conditions outlined from on the website racingtv.com - operated by Racing TV Limited.
    description: (val) => {
      return `Please read our terms and conditions outlined from on the website racingtv.com - operated by Racing TV Limited.`;
    },
  },
  PrivacyPolicy: {
    // title: Racing TV Privacy Policy
    title: (val) => {
      return `Racing TV Privacy Policy`;
    },
    // description: Please visit our website for more information relating to our privacy policy.
    description: (val) => {
      return `Please visit our website for more information relating to our privacy policy.`;
    },
  },
  RMGPressRelease: {
    // title: Racecourse Media Group Ltd (RMG) Press Releases
    title: (val) => {
      return `Racecourse Media Group Ltd (RMG) Press Releases`;
    },
    // description: Please visit our website to access all the latest press releases from Racecourse Media Group Ltd (RMG).
    description: (val) => {
      return `Please visit our website to access all the latest press releases from Racecourse Media Group Ltd (RMG).`;
    },
  },
  Competitions: {
    // title: Racing TV Competitions
    title: (val) => {
      return `Racing TV Competitions`;
    },
    // description: Win great prizes with Racing TV and our partners throughout the year with our Racing TV competitions.
    description: (val) => {
      return `Win great prizes with Racing TV and our partners throughout the year with our Racing TV competitions.`;
    },
  },
  ResponsibleGambling: {
    // title: Racing TV and Safer Gambling
    title: (val) => {
      return `Racing TV and Safer Gambling`;
    },
    // description: Please read our information to help you assess your gambling and take measures to stop if you need to.
    description: (val) => {
      return `Please read our information to help you assess your gambling and take measures to stop if you need to.`;
    },
  },
};

type Props = {
  page: string;
  tabs?: Tab[] | undefined;
  activeTab?: number | undefined;
  hardTitle?: string | undefined;
  hardDescription?: string | undefined;
  additionalData?: {} | undefined;
};

const UseAutoMetaTags = ({
  page,
  tabs = undefined,
  activeTab = undefined,
  hardTitle = undefined,
  hardDescription = undefined,
  additionalData = undefined,
}: Props) => {
  const setAutoMetaDescription = useSetAtom(autoMetaDescriptionAtom);
  const [pageTitle] = useAtom(pageTitleAtom);
  const isWeb = Platform.OS == "web";

  // auto generate page title and meta description
  // even though functionality is similar to calculate heading, its imporant to keep it outside.
  useFocusEffect(
    useCallback(() => {
      if (isWeb) {
        if (hardTitle && hardDescription) {
          window.document.title = hardTitle;
          setAutoMetaDescription(hardDescription);
          return;
        }
        // debugger;
        // we'd use page title every time but its not there on first render.
        // if (!pageTitle) return;

        // if (!additionalData) return;

        let titleVal = undefined;
        if (pageTitle || activeTab)
          titleVal = pageTitle ? pageTitle : tabs[activeTab].label;

        let pageTagMapEntry;
        try {
          pageTagMapEntry = pageTagMap[page];

          const metaTitle = !!pageTagMapEntry
            ? pageTagMapEntry.title(titleVal, additionalData)
            : seoTitleAndDescriptions[page]?.titles[titleVal](additionalData);

          // if no metaTitle is available, display "Racing TV as default"
          window.document.title = metaTitle || "Racing TV";

          const metaDescription = !!pageTagMapEntry
            ? pageTagMapEntry.description(titleVal, additionalData)
            : seoTitleAndDescriptions[page]?.descriptions[titleVal](
                additionalData
              );

          setAutoMetaDescription(metaDescription);
        } catch (e) {
          // its not usually a problem, we'll get it on the second render.
        }
      }
      return () => {
        // cleanup
        if (isWeb) {
          setAutoMetaDescription("");
          window.document.title = "";
          // setPageTitle("");
        }
      };
    }, [page, pageTitle, tabs, activeTab, additionalData])
  );
};

export default UseAutoMetaTags;
